import {
  Box,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  Text,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { ampli } from 'lib-shared-tracking';
import type {
  SearchConversationHit,
  SearchConversationResult,
} from 'lib-shared-types-api';
import { useCallback, useState } from 'react';
import { useUpdateEffect } from 'react-use';

import { searchApi } from 'apis/search';
import { ConversationsBulkToolbar } from 'components/conversation/conversationsBulkToolbar/conversationsBulkToolbar';
import { useSelectConversations } from 'utils/conversation/useSelectConversations/useSelectConversations';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';

import styles from '../../../searchResults.module.scss';
import { useSearchSplitLayout } from '../../../utils/useSearchSplitLayout';
import { SearchResultsEmpty } from '../../empty/searchResultsEmpty';
import { SearchResultsFeedback } from '../../feedback/searchResultsFeedback';
import { SearchResultsPagination } from '../../pagination/searchResultsPagination';
import { SearchResultsSelectAll } from '../../selectAll/searchResultsSelectAll';
import { SearchResultsSort } from '../../sort/searchResultsSort';

import { ConversationPreview } from './components/preview/conversationPreview';
import { ConversationResult } from './components/result/conversationResult';

type ConversationsResultsProps = {
  result?: SearchConversationResult;
};

/**
 * Represents a component that is used to render the conversations tab of the
 * search results
 */
export const ConversationsResults = ({
  result,
}: ConversationsResultsProps): JSX.Element => {
  const dispatch = useAppDispatch();

  const { params } = useAppSelector((state) => state.search);
  const { hasBulkOperations } = useFeatureFlag(['has-bulk-operations']);

  const [previewedHit, setPreviewedHit] =
    useState<SearchConversationHit | null>(null);

  const { leftColumnSize, rightColumnSize } = useSearchSplitLayout({
    isPreviewing: !!previewedHit,
  });

  const {
    asyncStatus: selectConversationsAsyncStatus,
    eligibleBulkConversationIds,
    isCurrentPageAllConversationsSelected,
    isCurrentPageSomeConversationsSelected,
    selectedConversationIds,
    totalFilteredConversations,
    selectAllCurrentPageConversation,
    selectAllEligibleConversations,
    toggleSelectConversation,
    resetSelection,
  } = useSelectConversations({
    currentPageConversationIds: result?.hits.map((hit) => hit.numeric_id) ?? [],
    onFetchConversationIdsBulk: async () => {
      const { data } = await dispatch(
        searchApi.endpoints.readAdvancedSearchConversationsIdsBulk.initiate({
          params: {
            scope: 'conversations',
            query: params.query,
            filters: params.filters,
            strict: params.strict,
          },
        }),
      ).unwrap();

      return data;
    },
  });

  // Handles resetting the selection when the search params change. This excludes the page param, as
  // this will change during pagination and we want to preserve the selection when that occurs.
  useUpdateEffect(() => {
    resetSelection();
  }, [params.query, params.filters, params.sort, params.strict]);

  const handleRowSelect = useCallback(
    ({ numeric_id }: SearchConversationHit) => {
      toggleSelectConversation(numeric_id);
    },
    [toggleSelectConversation],
  );

  const handleNavigateToConversation = useCallback(() => {
    if (!previewedHit) return;

    ampli.searchConversationPreviewGoToClicked({
      query: params.query || '',
      csid: previewedHit?.numeric_id.toString() || '',
    });
  }, [params.query, previewedHit]);

  const handleRowClick = useCallback(
    (hit: SearchConversationHit) => {
      if (hit.numeric_id === previewedHit?.numeric_id) return;

      setPreviewedHit(hit);
      ampli.searchConversationPreviewOpened({
        query: params.query || '',
        csid: hit.numeric_id.toString(),
      });
    },
    [params.query, previewedHit, setPreviewedHit],
  );

  const handlePreviewClose = useCallback(() => {
    setPreviewedHit(null);
    ampli.searchConversationPreviewClosed();
  }, [setPreviewedHit]);

  const showBulkToolbar = selectedConversationIds.length > 0;
  const isAllEligibleConversationsSelected =
    selectedConversationIds.length &&
    selectedConversationIds.length === eligibleBulkConversationIds.length;
  const hitsCount = result?.hits.length ?? 0;

  if (hitsCount === 0) {
    return <SearchResultsEmpty />;
  }

  return (
    <Columns>
      <Column size={leftColumnSize}>
        <Column.Row slottable borderBottom noMinHeight>
          <Column.Slot placement="start">
            <SearchResultsSelectAll
              totalCount={hitsCount}
              isAllSelected={isCurrentPageAllConversationsSelected}
              isSomeSelected={isCurrentPageSomeConversationsSelected}
              onChange={
                isAllEligibleConversationsSelected
                  ? resetSelection
                  : selectAllCurrentPageConversation
              }
            />
          </Column.Slot>

          <Column.Slot placement="end">
            <div className={styles.toolbars}>
              {hasBulkOperations && (
                <ConversationsBulkToolbar
                  className={classnames(styles.toolbar, styles.bulkActions, {
                    [styles.active]: showBulkToolbar,
                  })}
                  location="search"
                  asyncStatus={selectConversationsAsyncStatus}
                  eligibleBulkConversationIds={eligibleBulkConversationIds}
                  selectedConversationIds={selectedConversationIds}
                  totalFilteredConversations={totalFilteredConversations}
                  onSelectAll={selectAllEligibleConversations}
                />
              )}

              <SearchResultsSort
                className={classnames(styles.toolbar, styles.sort, {
                  [styles.active]: !showBulkToolbar,
                })}
              />
            </div>
          </Column.Slot>
        </Column.Row>

        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {result?.hits.map((hit, index) => {
            return (
              <ConversationResult
                className={classnames({
                  [styles.rowSelected]:
                    hit.numeric_id === previewedHit?.numeric_id,
                })}
                key={`${hit.numeric_id}-${index}`}
                hit={hit}
                isSelectable={hasBulkOperations}
                isSelected={selectedConversationIds.includes(hit.numeric_id)}
                onRowSelect={handleRowSelect}
                onRowClick={handleRowClick}
              />
            );
          })}
        </Column.Row>

        <Column.Row noVerticalPadding noHorizontalPadding borderTop>
          <SearchResultsPagination
            pageCount={result?.nb_pages ?? 1}
            currentPage={result?.page ?? 0}
          />
        </Column.Row>
      </Column>

      <Column size={rightColumnSize}>
        <Column.Row scrollable noVerticalPadding noHorizontalPadding>
          {previewedHit ? (
            <ConversationPreview
              csid={previewedHit.numeric_id}
              highlights={previewedHit._highlights}
              innerHits={previewedHit._inner_hits}
              onGoToConversation={handleNavigateToConversation}
              onPreviewClose={handlePreviewClose}
            />
          ) : (
            <Box padding="500 0">
              <EmptyState
                title="Select a conversation"
                description={
                  <FlexBox flexDirection="column" gap="300">
                    <Text.Paragraph>
                      Want to see a preview of that message you&apos;re looking
                      for? <br /> Go ahead and click a conversation to see it
                      here.
                    </Text.Paragraph>

                    <SearchResultsFeedback />
                  </FlexBox>
                }
              />
            </Box>
          )}
        </Column.Row>
      </Column>
    </Columns>
  );
};
